import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Button,
  message,
  notification,
  Typography,
  Spin,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { Link, useHistory } from "react-router-dom";
import { checkProfileExists, checkStatusByEmail } from "utils/api";
import {
  consumeNextParam,
  setPendingOAuthRedirect,
  clearPendingOAuthRedirect,
  login,
  sendVerificationEmail,
} from "utils/auth.service";
import {
  ACCOUNT_TYPE,
  ACCOUNT_TYPE_LABELS,
  BASE_URL,
  REDIRECTS,
} from "utils/consts";
import fireauth from "utils/fireauth";
import { fetchUser } from "features/userSlice";

// Resend button for the unverified-email notice. Self-contained so it can hold
// its own loading state (it's rendered imperatively inside an antd notification,
// outside the LoginForm render tree).
function ResendVerificationButton({ email }) {
  const { t } = useTranslation();
  const [resending, setResending] = useState(false);
  return (
    <Button
      type="primary"
      size="small"
      loading={resending}
      onClick={async () => {
        setResending(true);
        const verifyRes = await sendVerificationEmail(email);
        setResending(false);
        if (verifyRes && (verifyRes.success || verifyRes.status === 200)) {
          message.success(t("verifyEmail.emailResent"));
        } else {
          message.error(t("verifyEmail.error"));
        }
      }}
    >
      {t("verifyEmail.resend")}
    </Button>
  );
}

function LoginForm({ role, defaultEmail, n50_flag, location }) {
  if (!defaultEmail) {
    if (location && location.state && location.state.email) {
      defaultEmail = location.state.email;
    }
  }
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  useSelector((state) => state.user);
  const [loading, setLoading] = useState(false);
  const [isOAuthRedirecting, setIsOAuthRedirecting] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const roleLabel = t(`common.${ACCOUNT_TYPE_LABELS[role]}`);

  // Lock the OAuth next param at first render so it stays stable across
  // re-renders (useRef initializer runs exactly once per mount).
  const oauthNextCapturedRef = useRef(consumeNextParam());
  const oauthNextCaptured = oauthNextCapturedRef.current;

  // Arm the PublicRoute redirect gate synchronously on first render — a
  // useEffect would leave a one-commit window where login() could flip
  // localStorage.role before the gate is set, letting PublicRoute redirect
  // us off the login form. Cleared on unmount (covers both success and
  // error paths).
  if (oauthNextCaptured) setPendingOAuthRedirect();
  useEffect(() => clearPendingOAuthRedirect, []);

  // Perform the hard navigation only after React has painted the redirecting
  // overlay, so the user never sees the private dashboard.
  useEffect(() => {
    if (!isOAuthRedirecting || !oauthNextCaptured) return;
    window.location.href = BASE_URL.replace(/\/$/, "") + oauthNextCaptured;
  }, [isOAuthRedirecting]); // eslint-disable-line react-hooks/exhaustive-deps

  const onFinish = async ({ email, password }) => {
    if (role == null) return;
    setLoading(true);
    email = email.toLowerCase();

    // Non-admin checking for status of account
    if (
      role !== ACCOUNT_TYPE.ADMIN &&
      role !== ACCOUNT_TYPE.SUPPORT &&
      role !== ACCOUNT_TYPE.MODERATOR &&
      role !== ACCOUNT_TYPE.HUB
    ) {
      const { profileExists, rightRole } = await checkProfileExists(
        email,
        role
      );
      if (rightRole && parseInt(rightRole) !== role) {
        messageApi.error({
          content: t("loginErrors.roleMismatch", {
            role: t(`common.${ACCOUNT_TYPE_LABELS[parseInt(rightRole)]}`),
          }),
          duration: 0,
          key: "loginErrors.roleMismatch",
          onClick: () => messageApi.destroy("loginErrors.roleMismatch"),
        });
        setLoading(false);
        return;
      }

      const { inFirebase } = await checkStatusByEmail(email, role);
      if (profileExists === false && inFirebase === true) {
        //redirect to apply with role and email passed
        messageApi.info({
          content: t("loginErrors.profileIncomplete", { role: roleLabel }),
          duration: 0,
          key: "loginErrors.profileIncomplete",
          onClick: () => messageApi.destroy("loginErrors.profileIncomplete"),
        });
        history.push({
          pathname: n50_flag ? "/n50/build-profile" : "/build-profile",
          state: { email, role },
        });
        setLoading(false);
        return;
      } else if (profileExists === false && inFirebase === false) {
        messageApi.error({
          content: t("loginErrors.noAccountForRole", { role: roleLabel }),
          duration: 0,
          key: "loginErrors.noAccountForRole",
          onClick: () => messageApi.destroy("loginErrors.noAccountForRole"),
        });
        setLoading(false);
        return;
      }
    }

    const res = await login(
      email,
      password,
      role,
      location ? location.pathname : undefined
    );
    if (!res || !res.success) {
      if (res?.data?.result?.existingEmail) {
        messageApi.error({
          content: t("loginErrors.existingEmail"),
          duration: 0,
          key: "loginErrors.existingEmail",
          onClick: () => messageApi.destroy("loginErrors.existingEmail"),
        });
        setLoading(false);
      } else if (res?.data?.message) {
        messageApi.error({
          content: res.data.message,
          duration: 0,
          key: "loginErrors.serverMessage",
          onClick: () => messageApi.destroy("loginErrors.serverMessage"),
        });
        setLoading(false);
      } else {
        messageApi.error({
          content: t("loginErrors.incorrectCredentials"),
          duration: 0,
          key: "loginErrors.incorrectCredentials",
          onClick: () => messageApi.destroy("loginErrors.incorrectCredentials"),
        });
        setLoading(false);
      }
      return;
    } else if (res.result.passwordReset) {
      messageApi.error({
        content: t("loginErrors.resetPassword"),
        duration: 0,
        key: "loginErrors.resetPassword",
        onClick: () => messageApi.destroy("loginErrors.resetPassword"),
      });
      history.push("/forgot-password");
    } else if (res.result.recreateAccount) {
      messageApi.error({
        content: t("loginErrors.reregisterAccount"),
        duration: 0,
        key: "loginErrors.reregisterAccount",
        onClick: () => messageApi.destroy("loginErrors.reregisterAccount"),
      });
      history.push("/apply");
    }
    const unsubscribe = fireauth.auth().onAuthStateChanged(async (user) => {
      unsubscribe();
      if (!user) return;
      if (res.result.redirectToVerify) {
        // Email not verified yet. Show a persistent, dismissible notice with a
        // manual "Resend" button instead of silently re-sending the
        // verification email on every login (which spammed unverified users).
        // Wording doesn't claim we just sent one — they may already have it.
        notification.warning({
          key: "verify-email",
          message: t("verifyEmail.header"),
          description: t("verifyEmail.loginNotice"),
          duration: 0,
          btn: <ResendVerificationButton email={email} />,
        });
      }
      if (oauthNextCaptured) {
        // Show the redirecting overlay first; actual navigation fires in the
        // useEffect above so React paints before the page unloads. We also
        // skip fetchUser to avoid triggering a Redux re-render that would
        // briefly flash the private dashboard.
        setIsOAuthRedirecting(true);
        return;
      }
      dispatch(
        fetchUser({
          id: res.result.profileId,
          role,
        })
      );
      let direct_path = localStorage.getItem("direct_path");
      if (n50_flag) {
        localStorage.setItem("n50_user", true);
      }
      if (direct_path) {
        setTimeout(() => {
          history.push(direct_path);
        }, 1000);
        setTimeout(() => {
          localStorage.removeItem("direct_path");
        }, 2000);
      } else {
        if (role === ACCOUNT_TYPE.HUB) {
          history.push(location.pathname + REDIRECTS[role]);
        } else {
          history.push(REDIRECTS[role]);
        }
      }
    });
  };

  const onFinishFailed = (errorInfo) => {
    // console.log("Failed:", errorInfo);
  };

  if (isOAuthRedirecting) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          background: "#fff",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
          padding: "0 24px",
        }}
      >
        <Spin size="large" />
        <Typography.Title level={4} style={{ margin: 0, textAlign: "center" }}>
          {t("login.oauthRedirecting")}
        </Typography.Title>
        <Typography.Paragraph
          type="secondary"
          style={{ margin: 0, maxWidth: 420, textAlign: "center" }}
        >
          {t("login.oauthRedirectingBody")}
        </Typography.Paragraph>
      </div>
    );
  }

  return (
    <div
      className={css`
        display: flex;
        width: 100%;
        flex: 1;
        justify-content: center;
        flex-direction: column;

        @media (max-width: 991px) {
          flex: 0;
        }
      `}
    >
      {contextHolder}
      <Typography.Title level={2}>
        {t(`common.${ACCOUNT_TYPE_LABELS[role]}`)}
      </Typography.Title>
      <Form
        initialValues={{ remember: true }}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        autoComplete="off"
        layout="vertical"
        size="large"
        style={{ width: "100%" }}
      >
        <Form.Item
          name="email"
          label={t("common.email")}
          initialValue={defaultEmail}
          rules={[
            {
              required: true,
              type: "email",
              message: t("loginErrors.emailError"),
            },
          ]}
        >
          <Input
            type="email"
            inputMode="email"
            autoComplete="email"
            onChange={(e) => {
              form.setFieldsValue({ email: e.target.value.toLowerCase() });
            }}
            prefix={<UserOutlined />}
            autoFocus
          />
        </Form.Item>
        <div style={{ position: "relative" }}>
          <Form.Item
            name="password"
            label={t("common.password")}
            rules={[
              {
                required: true,
                message: t("loginErrors.passwordError"),
              },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} />
          </Form.Item>
          <div
            style={{
              width: "100%",
              textAlign: "end",
            }}
          >
            <Link to={{ pathname: "/forgot-password", state: { role } }}>
              {t("login.forgotPassword")}
            </Link>
          </div>
        </div>
        <Form.Item>
          <Button
            type="primary"
            id="submit"
            htmlType="submit"
            style={{ width: "100%" }}
            loading={loading}
            disabled={loading}
          >
            {t("common.login")}
          </Button>
          {role !== ACCOUNT_TYPE.ADMIN && (
            <div>
              <p
                className={css`
                  width: 100%;
                  text-align: center;
                  line-height: 0.1em;
                  position: relative;
                  padding-top: 8px;

                  &::before,
                  &::after {
                    content: "";
                    position: absolute;
                    top: 90%;
                    width: 40%;
                    height: 1px;
                    background: #000;
                    padding: 0 8px;
                  }

                  &::before {
                    left: 0;
                    margin-left: 4px;
                  }

                  &::after {
                    right: 0;
                    margin-right: 4px;
                  }
                `}
              >
                <span
                  className={css`
                    padding: 0 10px;
                  `}
                >
                  OR
                </span>
              </p>
              <div style={{ paddingTop: "4px", textAlign: "end" }}>
                <Link to={{ pathname: "/apply" }}>
                  <Button
                    style={{ width: "100%" }}
                    type="primary"
                    htmlType="button"
                  >
                    {t("login.registerUser")}
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </Form.Item>
      </Form>
    </div>
  );
}

export default LoginForm;
